import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookmarkButton from "../components/BookmarkButton";
import SEO, { entityJsonLd, breadcrumbJsonLd } from "../components/SEO";
import { trackEntityView } from "../lib/analytics";
import ShareDock from "../components/ShareDock";
import { ContinueInvestigationCTA } from "../components/PremiumLock";
import { GhostTopicChip, GhostEntityChip, GhostTimelineRow, GhostSourceCard } from "../components/GhostCards";
import RelatedVaccinesPanel from "../components/research/RelatedVaccinesPanel";
import { useSourceReader } from "../components/SourceReader";
import { Building2, User, Landmark, ExternalLink, ChevronLeft, Network, BookOpen, Calendar, ShieldCheck, ArrowRight, CornerDownRight } from "lucide-react";

const TYPE_META = {
    person: { Icon: User, label: "Person" },
    company: { Icon: Building2, label: "Company" },
    organization: { Icon: Landmark, label: "Organisation" },
};

function Section({ id, label, title, children }) {
    return (
        <section id={id} data-testid={`entity-section-${id}`} className="py-10 sm:py-12 border-t border-[#243044]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-3">
                {label}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-6">{title}</h2>
            <div className="text-[15px] leading-[1.75] text-[#CBD5E1]">{children}</div>
        </section>
    );
}

export default function EntityPage() {
    const { slug } = useParams();
    const [entity, setEntity] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const { openSource } = useSourceReader();

    useEffect(() => {
        setLoading(true);
        setError(null);
        api.get(`/entities/${slug}`)
            .then((r) => setEntity(r.data))
            .catch((e) => setError(e?.response?.status === 404 ? "Entity not found" : "Failed to load entity"))
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (entity?.slug) trackEntityView(entity.slug, { type: entity.type });
    }, [entity?.slug, entity?.type]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0B1220]">
                <Header />
                <div className="max-w-5xl mx-auto px-4 py-32 text-center text-sm text-[#64748B]">Loading entity…</div>
                <Footer />
            </div>
        );
    }

    if (error || !entity) {
        return (
            <div className="min-h-screen bg-[#0B1220]">
                <Header />
                <div className="max-w-5xl mx-auto px-4 py-32 text-center">
                    <h1 className="text-3xl font-bold text-white mb-3">{error || "Entity not found"}</h1>
                    <Link to="/entities" className="text-sm text-[#56CCF2] hover:text-white">← Back to entity index</Link>
                </div>
                <Footer />
            </div>
        );
    }

    const meta = TYPE_META[(entity.type || "").toLowerCase()] || TYPE_META.organization;
    const Icon = meta.Icon;
    const gate = entity._gate || { is_member: true };
    const isMember = !!gate.is_member;
    const counts = gate.counts || {};
    const ghosts = gate.ghosts || {};
    const totalTopics = counts.topics_total ?? entity.topics.length;
    const totalEvents = counts.events_total ?? entity.timeline_events.length;
    const totalCo = counts.co_entities_total ?? entity.co_entities.length;
    const totalSources = counts.sources_total ?? entity.sources.length;

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <SEO
                title={entity.name}
                description={entity.bio || entity.role || `Investigations and documented relationships involving ${entity.name}.`}
                canonicalPath={`/entity/${entity.slug}`}
                type="profile"
                jsonLd={[
                    entityJsonLd(entity),
                    breadcrumbJsonLd([
                        { name: "Library", path: "/" },
                        { name: "Entities", path: "/entities" },
                        { name: entity.name },
                    ]),
                ]}
            />
            <ShareDock
                title={entity.name}
                description={entity.bio || entity.role || `Investigations and documented relationships involving ${entity.name}.`}
                type="entity"
                id={entity.slug}
            />
            <Header />

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <Link to="/entities" className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#56CCF2] uppercase tracking-wider">
                    <ChevronLeft className="w-3 h-3" /> Back to entity index
                </Link>
            </div>

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-4">
                    {meta.label}
                </div>
                <div className="flex items-start gap-5 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#111827] border border-[#243044] flex items-center justify-center text-[#56CCF2] shrink-0">
                        <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 data-testid="entity-name" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-3">
                            {entity.name}
                        </h1>
                        {entity.role && (
                            <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed max-w-3xl">{entity.role}</p>
                        )}
                    </div>
                    <BookmarkButton
                        type="entity"
                        id={entity.slug}
                        title={entity.name}
                        subtitle={meta.label}
                        href={`/entity/${entity.slug}`}
                        size="md"
                        stopPropagation={false}
                    />
                </div>

                {entity.bio && (
                    <p className="text-[15px] text-[#CBD5E1] leading-relaxed max-w-3xl mb-6">{entity.bio}</p>
                )}

                {entity.url && (
                    <a
                        data-testid="entity-official-url"
                        href={entity.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[#56CCF2] hover:text-white transition-colors mb-8"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Official URL
                    </a>
                )}

                <div className="grid grid-cols-3 gap-3 max-w-2xl">
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-4 text-center">
                        <Network className="w-4 h-4 text-[#56CCF2] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white" data-testid="entity-stat-topics">{totalTopics}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#64748B] mt-1">Investigations</div>
                    </div>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-4 text-center">
                        <Calendar className="w-4 h-4 text-[#56CCF2] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white" data-testid="entity-stat-events">{totalEvents}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#64748B] mt-1">Timeline events</div>
                    </div>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-4 text-center">
                        <BookOpen className="w-4 h-4 text-[#56CCF2] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white" data-testid="entity-stat-sources">{totalSources}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#64748B] mt-1">Sources</div>
                    </div>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {/* Investigations */}
                {entity.topics.length > 0 && (
                    <Section id="investigations" label="01 / Connected investigations" title="Investigations referencing this entity">
                        <p className="text-xs text-[#64748B] uppercase tracking-[0.15em] mb-4">
                            Documented relationship · entity appears in topic record
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {entity.topics.map((t) => (
                                <Link
                                    key={t.slug}
                                    to={`/topic/${t.slug}`}
                                    data-testid={`entity-topic-${t.slug}`}
                                    className="group block bg-[#111827] border border-[#243044] rounded-xl p-5 hover:border-[#2F80ED] transition-colors"
                                >
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-medium mb-2">
                                        {t.category}
                                    </div>
                                    <p className="text-base font-semibold text-white group-hover:text-[#56CCF2] transition-colors leading-snug mb-2">
                                        {t.title}
                                    </p>
                                    {t.subtitle && (
                                        <p className="text-sm text-[#94A3B8] leading-relaxed line-clamp-2">{t.subtitle}</p>
                                    )}
                                </Link>
                            ))}
                        </div>
                        {!isMember && ghosts.topics?.length > 0 && (
                            <div className="mt-5 flex flex-wrap gap-2">
                                {ghosts.topics.map((g, i) => (
                                    <GhostTopicChip key={i} item={g} />
                                ))}
                            </div>
                        )}
                    </Section>
                )}

                {/* Timeline events */}
                {entity.timeline_events.length > 0 && (
                    <Section id="events" label="02 / Timeline appearances" title="Documented events involving this entity">
                        <p className="text-xs text-[#64748B] uppercase tracking-[0.15em] mb-4">
                            Newest first · via shared investigation
                        </p>
                        <div className="relative space-y-3">
                            <div className="absolute left-1.5 top-3 bottom-3 w-px bg-[#243044]" />
                            {entity.timeline_events.slice(0, 30).map((ev, i) => (
                                <div
                                    key={i}
                                    data-testid={`entity-event-${i}`}
                                    className="relative pl-8 bg-[#111827] border border-[#243044] rounded-xl p-4 hover:border-[#2F80ED]/50 transition-colors"
                                >
                                    <div className="absolute left-0 top-5 w-3 h-3 rounded-full bg-[#2F80ED] ring-4 ring-[#0B1220]" />
                                    <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#56CCF2] font-bold">{ev.date}</span>
                                        {ev.category && <span className="text-[10px] uppercase tracking-wider text-[#64748B]">· {ev.category}</span>}
                                    </div>
                                    <p className="text-sm font-semibold text-white mb-1">{ev.title}</p>
                                    {ev.description && (
                                        <p className="text-sm text-[#94A3B8] leading-relaxed line-clamp-2 mb-2">{ev.description}</p>
                                    )}
                                    <Link
                                        to={`/topic/${ev.topic_slug}`}
                                        className="text-[10px] uppercase tracking-wider text-[#64748B] hover:text-[#56CCF2] inline-flex items-center gap-1.5"
                                    >
                                        <CornerDownRight className="w-3 h-3" />
                                        from {ev.topic_title}
                                    </Link>
                                </div>
                            ))}
                            {isMember && entity.timeline_events.length > 30 && (
                                <p className="text-xs text-[#64748B] pl-8 pt-2 italic">
                                    Showing the 30 most recent events · {entity.timeline_events.length - 30} more in the full timeline.
                                </p>
                            )}
                            {!isMember && ghosts.timeline?.length > 0 && (
                                <div className="pl-8 pt-3 space-y-2">
                                    {ghosts.timeline.slice(0, 8).map((g, i) => (
                                        <GhostTimelineRow key={i} item={g} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                {/* Co-occurring entities */}
                {entity.co_entities.length > 0 && (
                    <Section id="co-entities" label="03 / Co-occurring entities" title="Entities that appear in the same investigations">
                        <p className="text-xs text-[#64748B] uppercase tracking-[0.15em] mb-4">
                            Via shared investigation · documented co-reference
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {entity.co_entities.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/entity/${c.slug}`}
                                    data-testid={`entity-co-${c.slug}`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#111827] border border-[#243044] text-sm text-[#CBD5E1] hover:border-[#2F80ED] hover:text-white transition-colors"
                                >
                                    {c.type === "person" ? <User className="w-3 h-3 text-[#56CCF2]" /> : c.type === "company" ? <Building2 className="w-3 h-3 text-[#56CCF2]" /> : <Landmark className="w-3 h-3 text-[#56CCF2]" />}
                                    <span>{c.name}</span>
                                    <span className="text-[10px] text-[#64748B]">×{c.shared_topics}</span>
                                </Link>
                            ))}
                            {!isMember && ghosts.co_entities?.length > 0 && (
                                ghosts.co_entities.map((g, i) => <GhostEntityChip key={`g-${i}`} item={g} />)
                            )}
                        </div>
                    </Section>
                )}

                {/* Sources */}
                {entity.sources.length > 0 && (
                    <Section id="sources" label="04 / Source map" title="Sources cited in investigations referencing this entity">
                        <p className="text-xs text-[#64748B] uppercase tracking-[0.15em] mb-4">
                            Via shared investigation · trace any source into the Source Vault
                        </p>
                        <ul className="space-y-3">
                            {entity.sources.map((s, i) => (
                                <li
                                    key={s.key || i}
                                    data-testid={`entity-source-${i}`}
                                    className="bg-[#111827] border border-[#243044] rounded-xl p-4 hover:border-[#2F80ED]/40 transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                                        <p className="text-sm font-semibold text-white leading-snug flex-1">{s.title}</p>
                                        {s.source_type_canonical && (
                                            <span className="text-[10px] uppercase tracking-wider text-[#64748B]">{s.source_type_canonical}</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-[#94A3B8] flex flex-wrap gap-x-3 gap-y-1 mb-2">
                                        {s.publisher && <span className="font-medium text-[#CBD5E1]">{s.publisher}</span>}
                                        {s.date && <span className="text-[#64748B]">· {s.date}</span>}
                                        {s.verified_at && (
                                            <span className="text-[10px] uppercase tracking-wider text-[#64748B] inline-flex items-center gap-1">
                                                <ShieldCheck className="w-3 h-3" />
                                                Verified {new Date(s.verified_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {s.url && (
                                            <a
                                                href={s.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => { if (e.metaKey||e.ctrlKey||e.shiftKey) return; e.preventDefault(); openSource(s); }}
                                                className="text-xs text-[#56CCF2] hover:text-white inline-flex items-center gap-1.5"
                                            >
                                                <ExternalLink className="w-3 h-3" /> View source
                                            </a>
                                        )}
                                        {s.key && (
                                            <Link
                                                to={`/sources?source=${encodeURIComponent(s.key)}`}
                                                className="text-xs text-[#94A3B8] hover:text-[#56CCF2] inline-flex items-center gap-1.5"
                                            >
                                                <ArrowRight className="w-3 h-3" /> Open in Vault
                                            </Link>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {!isMember && ghosts.sources?.length > 0 && (
                            <div className="mt-4 space-y-3">
                                {ghosts.sources.slice(0, 6).map((g, i) => (
                                    <GhostSourceCard key={i} item={g} />
                                ))}
                            </div>
                        )}
                    </Section>
                )}

                {/* Premium CTA — single calm card at the bottom for non-members */}
                {!isMember && (totalTopics > entity.topics.length
                    || totalEvents > entity.timeline_events.length
                    || totalCo > entity.co_entities.length
                    || totalSources > entity.sources.length) && (
                    <div className="mt-10">
                        <ContinueInvestigationCTA
                            label={`Open the full archive on ${entity.name}`}
                            subline="The entity hub continues — every documented investigation, co-occurring actor, full timeline arc, and source map. Members see the entire graph."
                            testId="entity-premium-cta"
                        />
                    </div>
                )}
                {entity?.id ? (
                    <div className="mt-8">
                        <RelatedVaccinesPanel entityId={entity.id} parentRef={`entity:${entity.slug}`} />
                    </div>
                ) : null}
            </div>

            <Footer />
        </div>
    );
}
